import type { BookingResponse, GuestProfileResponse, InvoiceResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/format';
import { formatBookingPrice } from '@/shared/lib/bookingPrice';
import { UpgradeChargesCard } from '../upgrade-charge';
import { BillingCard } from './BillingCard';
import { InvoiceRow } from './InvoiceRow';
import { OtaRefundPanel } from './OtaRefundPanel';
import { SectionCard } from './SectionCard';
import { summarizeInvoiceGroups } from './bookingInvoiceModel';

interface Props {
  booking: BookingResponse;
  /** `null`: Rechnungen nicht geladen – dann keine Summen, die nach „nichts“ aussehen. */
  invoices: InvoiceResponse[] | null;
  guest?: GuestProfileResponse | null;
}

/**
 * Reiter Geld (Board „BuchungGeld“): vier Zahlen, darunter Rechnungen, Aufpreise, Erstattungen
 * (nur Kanalbuchungen) und die Abrechnung. Offen bleibt Bernstein, bezahlt wird grün.
 */
export function BookingBillingWorkspace({ booking, invoices, guest }: Props) {
  const { currency } = booking;
  const summary = invoices ? summarizeInvoiceGroups(invoices, currency) : null;
  const known = summary && !summary.currencyMismatch;
  const none = invoices?.length === 0;
  const figures: Array<{ label: string; value: string; tone?: 'amber' | 'green' }> = [
    { label: 'Buchungspreis', value: formatBookingPrice(booking) },
    {
      label: 'Rechnungen aktiv',
      value: !summary
        ? '–'
        : none
          ? 'keine'
          : known
            ? formatMoney(summary.activeTotal, currency)
            : 'andere Währung',
    },
    {
      label: 'Offen',
      value: known && !none ? formatMoney(summary.openTotal, currency) : '–',
      tone: 'amber',
    },
    {
      label: 'Bezahlt',
      value: known && !none ? formatMoney(summary.paidTotal, currency) : '–',
      tone: 'green',
    },
  ];
  return (
    <div className="@container flex flex-col gap-4">
      <section
        aria-label="Geld in Zahlen"
        className="border-hairline rounded-tile @2xl:grid-cols-4 grid grid-cols-2 gap-4 border bg-white p-5"
      >
        {figures.map((figure) => (
          <div key={figure.label} className="flex min-w-0 flex-col gap-1">
            <span className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
              {figure.label}
            </span>
            <span
              className={cn(
                'truncate font-serif text-2xl font-bold',
                figure.value !== '–' && figure.tone === 'amber' && 'text-amber-ink',
                figure.value !== '–' && figure.tone === 'green' && 'text-forest',
              )}
            >
              {figure.value}
            </span>
          </div>
        ))}
      </section>
      <SectionCard
        icon="Receipt"
        title="Rechnungen"
        trailing={invoices && invoices.length > 0 ? <Count n={invoices.length} /> : undefined}
      >
        <InvoiceRow booking={booking} guest={guest} />
      </SectionCard>
      <UpgradeChargesCard bookingId={booking.id} />
      <OtaRefundPanel booking={booking} />
      <BillingCard booking={booking} />
    </div>
  );
}

function Count({ n }: { n: number }) {
  return <span className="rounded-pill bg-card-alt px-2.5 py-1 text-xs font-bold">{n}</span>;
}
